import { useConfigurator } from '@/app/store/configurator/hooks/use-configurator';
import {
	RACK_MATERIAL_COLORS,
	SIDE_BORDER_WIDTH,
	SIDE_EDITOR_TOOLS_GAP,
	SIDE_EDITOR_TOOLS_HEIGHT,
} from '@/shared/constants/configurator';
import { DISPLAY_SCALE } from '@/shared/constants/editor';
import { Size } from '@/shared/ui/size/size';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
	SIDE_SECTION_BORDER_WIDTH,
	SIDE_SECTION_ENDS_DISTANCE,
} from '../../constants/section.constants';
import { SectionSideProps } from '../../types/section.types';
import { SectionBorder } from '../section-border/section-border';
import { SideSectionBorderChunk } from '../side-section-border-chunk/side-section-border-chunk';
import styles from './side-section.module.css';

export const SideSection = ({ section }: SectionSideProps) => {
	const {
		state: { material, sections },
	} = useConfigurator();
	const { t } = useTranslation();

	const borderChunkOptions = useMemo(() => {
		const count = Math.floor(section.height / 1000);
		const height =
			(count * 1000 * DISPLAY_SCALE - SIDE_SECTION_ENDS_DISTANCE) / count +
			SIDE_SECTION_BORDER_WIDTH;

		return {
			count,
			height,
		};
	}, [sections]);

	const getChunkBottomStyle = (index: number) => {
		const initialBottom = -SIDE_SECTION_BORDER_WIDTH;

		if (!index) {
			return initialBottom;
		}

		return initialBottom + index * (borderChunkOptions.height - SIDE_SECTION_BORDER_WIDTH);
	};

	return (
		<div
			style={{
				height:
					section.height * DISPLAY_SCALE +
					SIDE_EDITOR_TOOLS_HEIGHT +
					SIDE_EDITOR_TOOLS_GAP,
				gap: SIDE_EDITOR_TOOLS_GAP,
			}}
		>
			<div
				className={styles.section}
				style={{
					height: section.height * DISPLAY_SCALE,
					width: section.depth * DISPLAY_SCALE,
				}}
			>
				<SectionBorder type="side" className={styles.leftSection} />
				<SectionBorder type="side" className={styles.rightSection} />
				<div className={styles.sectionsContent}>
					<div
						style={{ borderTopColor: RACK_MATERIAL_COLORS[material] }}
						className={styles.sectionChunkContent}
					>
						{Array.from({ length: borderChunkOptions.count }, (_, index) => (
							<SideSectionBorderChunk
								className={styles.sectionChunk}
								height={borderChunkOptions.height}
								width={
									section.depth * DISPLAY_SCALE -
									SIDE_BORDER_WIDTH * DISPLAY_SCALE * 2
								}
								side={index % 2 === 0 ? 'left' : 'right'}
								key={index}
								style={{
									bottom: getChunkBottomStyle(index),
								}}
							/>
						))}
					</div>
				</div>
			</div>
			<div
				style={{
					height: SIDE_EDITOR_TOOLS_HEIGHT,
					maxWidth: section.depth * DISPLAY_SCALE,
				}}
				className={styles.sizeWrapper}
			>
				<Size
					className={styles.widthSize}
					type="width"
					value={`${section.depth} ${t('units.mm')}`}
				/>
			</div>
		</div>
	);
};
